import React from "react";
import { CheckCircleOutlined, DashboardOutlined } from "@ant-design/icons";
import SbTour from "uiCore/SbTour/SbTour";
import useModuleTour from "uiCore/SbTour/useModuleTour";
import buildTourSteps from "uiCore/SbTour/buildTourSteps";
import { TOUR_MODULE_KEY, getTourSteps } from "../dashboardTour.constants";

/**
 * DashboardTour — guided tour for the Work Transformation Dashboard.
 *
 * Auto-launches once per user and registers with the global "Take the Tour"
 * button. Dashboard only mounts it once data has loaded, so it never runs on
 * the no-access / no-data screens or against skeleton layouts.
 *
 * @param {Object}   targetRefs          { filterBar, widgets, topSkills, collaboration, totalPotential, roi }
 * @param {boolean}  canViewDollarValue  Picks the $ or hours-only Total Potential step.
 * @param {Function} onApplyFilter       Finish CTA — scrolls to and pulses the filter bar.
 */
const DashboardTour = ({ targetRefs, canViewDollarValue, onApplyFilter }) => {
  const { open, current, remountKey, goToStep, close, markSeen } = useModuleTour({
    moduleKey: TOUR_MODULE_KEY,
  });

  const tourSteps = getTourSteps(canViewDollarValue);

  const closeTour = () => {
    markSeen();
    close();
  };

  const finishTour = () => {
    closeTour();
    onApplyFilter?.();
  };

  const { steps, actionsRender, indicatorsRender } = buildTourSteps(tourSteps, {
    resolveTarget: (step) => targetRefs[step.key]?.current,
    greeting: {
      icon: (step, isLast) => (isLast ? <CheckCircleOutlined /> : <DashboardOutlined />),
      onPrimary: (step, index, isLast) => (isLast ? finishTour() : goToStep(index + 1)),
      onSecondary: () => closeTour(),
    },
  });

  return (
    <SbTour
      key={remountKey}
      open={open}
      current={current}
      onChange={goToStep}
      steps={steps}
      onClose={closeTour}
      onFinish={closeTour}
      actionsRender={actionsRender}
      indicatorsRender={indicatorsRender}
    />
  );
};

export default DashboardTour;
